import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import LanguageLogo from '@/components/Logo';
import { LANDING } from '@/constants/testIds';

export default function Header() {
  const { lang, toggle, isEnglish } = useLanguage();
  const t = translations[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <motion.header
      data-testid={LANDING.header}
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? 'bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#E5DFD3] shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <button onClick={() => scrollTo('hero')} className="flex items-center group">
            <LanguageLogo
              lang={lang}
              variant={scrolled ? 'default' : 'white'}
              size="md"
              showTagline={true}
            />
          </button>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-5">
            {/* Language Toggle */}
            <div className="flex items-center gap-1.5 sm:gap-2" data-testid={LANDING.langToggle}>
              <span className={`text-[10px] sm:text-xs font-semibold transition-colors ${
                scrolled ? 'text-[#5C6A61]' : 'text-white/80'
              } ${isEnglish ? 'opacity-100' : 'opacity-50'}`}>
                EN
              </span>
              <Switch
                checked={!isEnglish}
                onCheckedChange={toggle}
                className="data-[state=checked]:bg-[#D48C57] data-[state=unchecked]:bg-[#2C4C3B]/40"
                aria-label="Toggle language between English and Tamil"
              />
              <span
                data-testid={LANDING.langLabel}
                className={`text-[10px] sm:text-xs font-semibold transition-colors ${
                  scrolled ? 'text-[#5C6A61]' : 'text-white/80'
                } ${!isEnglish ? 'opacity-100' : 'opacity-50'}`}
              >
                தமிழ்
              </span>
            </div>

            {/* Phone CTA - Desktop */}
            <a
              href="tel:+919403890991"
              data-testid={LANDING.headerPhone}
              className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 ${
                scrolled
                  ? 'bg-[#2C4C3B] text-white hover:bg-[#213A2D]'
                  : 'bg-white/15 text-white backdrop-blur-sm hover:bg-white/25'
              }`}
            >
              <Phone className="w-3.5 h-3.5" />
              {t.phone}
            </a>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`sm:hidden p-2 rounded-lg transition-colors ${
                scrolled ? 'text-[#1A2520]' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="sm:hidden overflow-hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#E5DFD3]"
          >
            <div className="px-4 py-4 flex flex-col gap-3">
              {['plots', 'infrastructure', 'location'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollTo(section)}
                  className="text-left text-sm font-medium text-[#1A2520] py-2 border-b border-[#E5DFD3]/50 capitalize"
                >
                  {section === 'plots' ? (isEnglish ? 'Plots' : 'பிளாட்டுகள்') :
                   section === 'infrastructure' ? (isEnglish ? 'Infrastructure' : 'அடிப்படை வசதிகள்') :
                   isEnglish ? 'Location' : 'அமைவிடம்'}
                </button>
              ))}
              <a
                href="tel:+919403890991"
                className="flex items-center justify-center gap-2 bg-[#2C4C3B] text-white py-3 rounded-xl text-sm font-medium"
              >
                <Phone className="w-4 h-4" />
                {t.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
